import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    FaRobot, FaPaperPlane, FaFilePdf, 
    FaVideo, FaImage, FaLanguage, FaMagic, FaFileAlt,
    FaClosedCaptioning, FaListUl, FaSearch, FaPlayCircle,
    FaCalendarAlt, FaEye, FaDownload, FaSpinner,
    FaExclamationTriangle
} from 'react-icons/fa';
import Nav from '../components/Nav';
import VideoModal from '../components/VideoModal';
import { useAuth } from '../context/AuthContext';
import { useSession } from '../context/SessionContext';
import { resourcesAPI } from '../services/api';
import './PatientAI.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const PatientAI = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { sessionLanguage } = useSession();
    
    const [messages, setMessages] = useState([]);
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedReference, setSelectedReference] = useState(null);
    const [activeMsgIndex, setActiveMsgIndex] = useState(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [videoResource, setVideoResource] = useState(null);

    const [blobUrl, setBlobUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const [isTranslated, setIsTranslated] = useState(false);
    const [translatedText, setTranslatedText] = useState(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const [showError, setShowError] = useState(null);

    // Ref for auto-scrolling to bottom of chat
    const chatEndRef = useRef(null);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleTranslateDoc = async () => {
        console.log("🌐 [PatientAI] handleTranslateDoc called for:", selectedReference);
        if (!selectedReference || selectedReference.type !== 'pdf') {
            console.warn("⚠️ [PatientAI] Cannot translate: reference is not a PDF", selectedReference);
            return;
        }
        
        setIsTranslating(true);
        try {
            console.log(`🤖 [PatientAI] Requesting translation for ID ${selectedReference.id} to language: ${sessionLanguage}`);
            const response = await fetch(`${API_BASE}/resources/${selectedReference.id}/translate-doc`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ targetLanguage: sessionLanguage === 'es' ? 'Spanish' : 'English' })
            });
            const data = await response.json();
            if (data.success) {
                console.log("✅ [PatientAI] Translation successful");
                setTranslatedText(data.translatedContent);
                setIsTranslated(true);
            } else {
                console.error("❌ [PatientAI] Translation API error:", data.msg);
                setShowError(data.msg || "Error en la traducción");
            }
        } catch (error) {
            console.error("❌ [PatientAI] Fetch error during translation:", error);
            setShowError("Error de conexión al intentar traducir el documento. Asegúrate de que el servidor de IA esté funcionando.");
        } finally {
            setIsTranslating(false);
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, loading]);

    useEffect(() => {
        const fetchBlob = async () => {
            if (!selectedReference || (selectedReference.type !== 'pdf' && selectedReference.type !== 'image')) {
                if (blobUrl) {
                    URL.revokeObjectURL(blobUrl);
                    setBlobUrl(null);
                }
                setIsTranslated(false);
                setTranslatedText(null);
                return;
            }

            // Reset translation when switching resources
            setIsTranslated(false);
            setTranslatedText(null);

            // Si es un mock image artifact (empieza con /), no intentamos descargar vía API
            if (selectedReference.url.startsWith('/') && selectedReference.type === 'image') {
                setBlobUrl(selectedReference.url);
                return;
            }

            try {
                setPreviewLoading(true);
                const response = await resourcesAPI.download(selectedReference.id, { preview: 'true' });
                const url = URL.createObjectURL(response.data);
                setBlobUrl(url);
                setPreviewLoading(false);
            } catch (error) {
                console.error("Error fetching resource blob:", error);
                setPreviewLoading(false);
            }
        };

        fetchBlob();

        return () => {
            if (blobUrl && !blobUrl.startsWith('/')) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, [selectedReference]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSend = async (customPrompt = null) => {
        const textToSend = customPrompt || question;
        if (!textToSend.trim()) return;

        const userMsg = { role: 'user', text: textToSend };
        const newMessages = [...messages, userMsg];
        setMessages(newMessages);
        if (!customPrompt) setQuestion('');
        setLoading(true);

        try {
            const response = await fetch(`${API_BASE}/ai/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message: textToSend,
                    userRole: 'paciente'
                })
            });

            const data = await response.json();
            
            // Mock references for demonstration
            const mockRefs = [
                { 
                    id: 1, 
                    title: 'Análisis de Datos de Salud 2024', 
                    type: 'image', 
                    url: '/medical_infographic_thumbnail_1777790047476.png', 
                    thumbnail: '/medical_infographic_thumbnail_1777790047476.png' 
                },
                { 
                    id: 21, 
                    title: 'Investigación sobre Leucemia', 
                    type: 'video', 
                    url: `${API_BASE}/resources/21/download`, 
                    thumbnail: '/medical_video_thumbnail_1777790088683.png',
                    description: 'Estudio clínico detallado sobre los avances en el tratamiento de la leucemia y análisis de casos.',
                    date: '28 de Abril, 2024',
                    views: '2.4k'
                },
                { 
                    id: 32, 
                    title: 'Protocolo de Recuperación Post-Op', 
                    type: 'pdf', 
                    url: `${API_BASE}/resources/32/download`, 
                    thumbnail: '📄' 
                }
            ];

            const aiMsg = { 
                role: 'ai', 
                text: data.response,
                references: mockRefs
            };
            
            const updatedMessages = [...newMessages, aiMsg];
            setMessages(updatedMessages);
            setActiveMsgIndex(updatedMessages.length - 1);
            setLoading(false);
        } catch (error) {
            console.error('Error in AI Assistant:', error);
            setMessages(prev => [...prev, { role: 'ai', text: 'Lo siento, hubo un error al conectar con el asistente de salud.' }]);
            setLoading(false);
        }
    };

    const handlePlayVideo = (ref) => {
        setVideoResource({
            id: ref.id,
            title: ref.title,
            fileName: ref.title + '.mp4',
            url: ref.url
        });
        setIsModalOpen(true);
    };

    const renderPreview = () => {
        if (!selectedReference) {
            return (
                <div className="pai-empty-preview">
                    <FaFileAlt className="empty-icon" />
                    <h3>Visor de Recursos</h3>
                    <p>Selecciona un recurso para visualizarlo aquí.</p>
                </div>
            );
        }

        if (previewLoading) {
            return (
                <div className="pai-preview-loading">
                    <FaSpinner className="spinner-icon" />
                    <p>Cargando vista previa...</p>
                </div>
            );
        }

        if (selectedReference.type === 'video') {
            return (
                <div className="pai-resource-preview video-preview-mode">
                    <div className="res-card-preview">
                        <div className="res-card-thumb-preview" style={{ backgroundImage: `url(${selectedReference.thumbnail})` }}>
                            <div className="thumb-overlay">
                                <FaPlayCircle className="large-play-icon" onClick={() => handlePlayVideo(selectedReference)} />
                            </div>
                            <span className="thumb-badge-preview">VIDEO</span>
                            <span className="thumb-duration-preview">4:25 min</span>
                        </div>
                        <div className="res-card-body-preview">
                            <h3 className="res-card-title-preview">{selectedReference.title}</h3>
                            <p className="res-card-desc-preview">
                                {selectedReference.description || 'Este video contiene información detallada sobre el tema consultado, incluyendo explicaciones visuales y análisis de expertos.'}
                            </p>
                            <div className="res-card-meta-preview">
                                <span><FaCalendarAlt /> {selectedReference.date || 'May 2024'}</span>
                                <span><FaEye /> {selectedReference.views || '1.2k'} vistas</span>
                            </div>
                            <button className="res-card-btn-preview" onClick={() => handlePlayVideo(selectedReference)}>
                                <FaPlayCircle /> Reproducir ahora
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="pai-resource-preview document-preview-mode">
                <header className="preview-header">
                    <div className="preview-title-wrap">
                        <span className="preview-type">{selectedReference.type.toUpperCase()}</span>
                        <h2>{selectedReference.title} {isTranslated && <span className="translated-badge">(Traducido)</span>}</h2>
                    </div>
                    <div className="preview-actions">
                        {selectedReference.type === 'pdf' && (
                            <button 
                                className={`preview-translate-btn ${isTranslating ? 'loading' : ''}`}
                                onClick={handleTranslateDoc}
                                disabled={isTranslating}
                            >
                                {isTranslating ? (
                                    <>
                                        <FaSpinner className="spin" /> Traduciendo...
                                    </>
                                ) : (
                                    <>
                                        <FaMagic /> Traducir con IA
                                    </>
                                )}
                            </button>
                        )}
                        <a href={selectedReference.url} download className="preview-download-btn" title="Descargar original">
                            <FaDownload />
                        </a>
                    </div>
                </header>

                {isTranslated && (
                    <div className="translation-toggle-bar">
                        <button 
                            className={`toggle-btn ${!isTranslated ? 'active' : ''}`}
                            onClick={() => setIsTranslated(false)}
                        >
                            Original
                        </button>
                        <button 
                            className={`toggle-btn ${isTranslated ? 'active' : ''}`}
                            onClick={() => setIsTranslated(true)}
                        >
                            Traducido (IA)
                        </button>
                    </div>
                )}

                <div className="preview-content-view">
                    {selectedReference.type === 'pdf' && isTranslated && translatedText && (
                        <div className="translated-text-view">
                            <div className="translated-header">
                                <FaMagic /> Traducción generada por MedLlama
                            </div>
                            <div className="translated-body">
                                {translatedText.split('\n').map((line, idx) => (
                                    <p key={idx}>{line}</p>
                                ))}
                            </div>
                        </div>
                    )}
                    {selectedReference.type === 'pdf' && !isTranslated && blobUrl && (
                        <div className="pdf-embed-wrap">
                            <embed
                                src={`${blobUrl}#toolbar=1&view=FitH`}
                                type="application/pdf"
                                className="pdf-embed-element"
                            />
                        </div>
                    )}
                    {selectedReference.type === 'pdf' && !isTranslated && !blobUrl && (
                        <div className="pdf-view-placeholder">
                            <FaFilePdf />
                            <p>No se pudo cargar el documento.</p>
                        </div>
                    )}
                    {selectedReference.type === 'image' && (
                        <div className="image-view-enlarged">
                            <img src={blobUrl || selectedReference.url} alt={selectedReference.title} />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const activeReferences = activeMsgIndex !== null ? messages[activeMsgIndex]?.references || [] : [];

    return (
        <div className="pai-page-3col">
            <Nav hideMessages={true} showBackButton={true} />
            <div className="pai-layout-wrapper">
                <div className="pai-main-3col">
                    <div className="pai-col-chat">
                        <div className="pai-chat-frame">
                            <div className="pai-chat-scroll-area">
                            <div className="pai-messages-list-3col">
                                {messages.map((msg, i) => (
                                    <div 
                                        key={i} 
                                        className={`pai-msg-block ${msg.role} ${activeMsgIndex === i ? 'msg-active' : ''}`}
                                        onClick={() => msg.role === 'ai' && setActiveMsgIndex(i)}
                                    >
                                        <div className="msg-bubble-3col">
                                            {msg.text}
                                        </div>
                                    </div>
                                ))}
                                {loading && (
                                    <div className="pai-msg-block ai loading">
                                        <div className="typing-3dots">
                                            <span></span><span></span><span></span>
                                        </div>
                                    </div>
                                )}
                                <div ref={chatEndRef} />
                            </div>
                        </div>
                    </div>

                        <div className="pai-input-col-wrapper">
                            <div className="pai-quick-actions-row">
                                <button className="qa-btn" onClick={() => handleSend("Traduce la respuesta")}>
                                    <FaLanguage /> Traducir
                                </button>
                                <button className="qa-btn" onClick={() => handleSend("Simplifica la última explicación")}>
                                    <FaMagic /> Lenguaje Sencillo
                                </button>
                            </div>
                            <div className="pai-input-bar-3col">
                                <input
                                    type="text"
                                    placeholder="Realiza una consulta..."
                                    value={question}
                                    onChange={(e) => setQuestion(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                    disabled={loading}
                                />
                                <button className="pai-send-circular" onClick={() => handleSend()} disabled={loading || !question.trim()}>
                                    <FaPaperPlane />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="pai-col-refs">
                        <div className="pai-refs-header">
                            <FaListUl />
                            <h3>Recursos</h3>
                        </div>
                        <div className="pai-refs-scroll">
                            {activeReferences.length > 0 ? (
                                activeReferences.map(ref => (
                                    <div 
                                        key={ref.id} 
                                        className={`pai-ref-card ${selectedReference?.id === ref.id ? 'ref-selected' : ''}`}
                                        onClick={() => setSelectedReference(ref)}
                                    >
                                        <div className="ref-icon-wrap">
                                            {ref.thumbnail.startsWith('/') ? (
                                                <img src={ref.thumbnail} alt={ref.title} className="ref-thumb-img" />
                                            ) : (
                                                ref.thumbnail
                                            )}
                                        </div>
                                        <div className="ref-info-wrap">
                                            <span className="ref-tag">{ref.type.toUpperCase()}</span>
                                            <h4 className="ref-title-text">{ref.title}</h4>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="pai-refs-empty">
                                    <FaSearch />
                                    <p>Los recursos aparecerán aquí.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="pai-col-preview">
                        {renderPreview()}
                    </div>
                </div>
            </div>
            <VideoModal 
                isOpen={isModalOpen} 
                resource={videoResource} 
                onClose={() => setIsModalOpen(false)} 
            />
            {/* Error Dialog */}
            {showError && (
                <div className="pai-modal-overlay">
                    <div className="pai-error-dialog">
                        <div className="error-dialog-header">
                            <FaExclamationTriangle />
                            <h3>Error de Procesamiento</h3>
                        </div>
                        <p>{showError}</p>
                        <div className="error-dialog-actions">
                            <button onClick={() => setShowError(null)}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatientAI;
